import React, { ReactNode } from "react";
import { getLanguageInfo } from "@/utils/localization/language";
import { FlagIcon } from "./FlagIcon";

interface LanguageTooltipProps {
  languages: Array<{
    language: string;
    accent?: string;
    locale?: string;
  }>;
  children: ReactNode;
  maxDisplay?: number;
}

export const LanguageTooltip: React.FC<LanguageTooltipProps> = ({ languages, children, maxDisplay = 4 }) => {
  if (!languages || languages.length === 0) return null;

  // Build tooltip content as HTML list
  const tooltipItems = languages
    .slice(0, 10) // Show max 10 in tooltip
    .map((l) => {
      const info = getLanguageInfo(l.language);
      const parts = [info.name];
      if (l.accent) parts.push(l.accent);
      return `${info.flag} ${parts.join(" - ")}`;
    });

  const remainingCount = languages.length > 10 ? languages.length - 10 : 0;

  // Create a formatted tooltip with larger text
  const fullTooltip = tooltipItems.join("\n") + (remainingCount > 0 ? `\n... and ${remainingCount} more` : "");

  return (
    <div className="inline-flex items-center gap-1 group relative">
      {children}
      {/* Custom tooltip on hover */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-pre-line min-w-[150px] hidden group-hover:block">
        <div className="text-sm leading-relaxed">
          {tooltipItems.map((item, idx) => (
            <div key={idx} className="py-0.5">
              {item}
            </div>
          ))}
          {remainingCount > 0 && (
            <div className="pt-1 text-gray-400 text-xs border-t border-gray-700 mt-1">... and {remainingCount} more</div>
          )}
        </div>
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
          <div className="border-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact language flag display with tooltip
 */
export const CompactLanguageFlags: React.FC<{
  languages: Array<{ language: string; accent?: string; locale?: string }>;
  maxDisplay?: number;
  size?: "sm" | "base" | "lg";
}> = ({ languages, maxDisplay = 4, size = "sm" }) => {
  if (!languages || languages.length === 0) return null;

  // Deduplicate languages based on the language string itself
  const uniqueLanguages = languages.reduce((acc, lang) => {
    // Use just the language value as the key since it might contain both code and variant
    const key = lang.language || "en";
    if (!acc.find((l) => (l.language || "en") === key)) {
      acc.push(lang);
    }
    return acc;
  }, [] as typeof languages);

  const displayLanguages = uniqueLanguages.slice(0, maxDisplay);
  const remainingLanguages = uniqueLanguages.slice(maxDisplay);
  const remainingCount = remainingLanguages.length;

  return (
    <div className="inline-flex items-center gap-1">
      {displayLanguages.map((lang, idx) => {
        const info = getLanguageInfo(lang.language);
        const tooltipText = `${info.flag} ${info.name}${lang.accent ? ` (${lang.accent})` : ""}`;

        return (
          <div key={idx} className="group/flag relative">
            <FlagIcon language={lang.language} accent={lang.accent} size={size} />
            {/* Individual flag tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover/flag:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap hidden group-hover/flag:block shadow-lg">
              {tooltipText}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                <div className="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        );
      })}

      {remainingCount > 0 && (
        <div className="group/more relative">
          <span className="text-xs text-gray-500 ml-0.5 cursor-default">+{remainingCount}</span>
          {/* Tooltip showing ALL remaining languages */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white rounded-lg opacity-0 group-hover/more:opacity-100 transition-opacity duration-200 pointer-events-none z-50 min-w-[200px] max-w-[400px] hidden group-hover/more:block shadow-lg">
            <div className="text-sm font-medium mb-1 text-gray-300">Other languages:</div>
            <div className="space-y-1">
              {remainingLanguages.map((lang, idx) => {
                const info = getLanguageInfo(lang.language);
                return (
                  <div key={idx} className="text-sm leading-relaxed whitespace-normal">
                    {info.flag} {info.name}
                    {lang.accent ? ` (${lang.accent})` : ""}
                  </div>
                );
              })}
            </div>
            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
